/**
 * SidePanel.tsx — collapsible right-rail with chat, dice roller,
 * event log, and zone counts (deck/abyss/banished).
 *
 * Phase 1 ships chat + dice + event log. Voice / friends / spectator
 * land in Phase 2.
 */

import clsx from 'clsx';
import { useState } from 'react';
import type { ChatEntry, DiceRoll, PlayerId, PublicTableState } from '../types.js';

type Props = {
  state: PublicTableState;
  viewer: PlayerId;
  onSendChat: (text: string) => void;
  onRollDice: (sides: number) => void;
  onConcede: () => void;
  onDeclareVictory: () => void;
};

export function SidePanel({
  state,
  viewer,
  onSendChat,
  onRollDice,
  onConcede,
  onDeclareVictory,
}: Props) {
  const [tab, setTab] = useState<'chat' | 'log' | 'dice'>('chat');
  const [draft, setDraft] = useState('');

  const sendChat = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onSendChat(trimmed);
    setDraft('');
  };

  return (
    <aside className="flex flex-col flex-1 bg-cosmos-900/80 backdrop-blur min-h-0">
      {/* Tabs */}
      <div className="flex border-b border-cosmos-700">
        {(['chat', 'log', 'dice'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={clsx(
              'flex-1 py-2 text-sm uppercase tracking-wide transition',
              tab === t
                ? 'bg-cosmos-700 text-cosmos-50 border-b-2 border-amber-400'
                : 'text-cosmos-400 hover:bg-cosmos-700/50',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-3">
        {tab === 'chat' && <ChatTab chat={state.chat} viewer={viewer} />}
        {tab === 'log' && <LogTab dice={state.dice} chat={state.chat} viewer={viewer} />}
        {tab === 'dice' && <DiceTab dice={state.dice} onRoll={onRollDice} />}
      </div>

      {/* Chat input (always visible regardless of tab) */}
      <div className="border-t border-cosmos-700 p-2 flex gap-1">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') sendChat();
          }}
          placeholder="Say something…"
          className="flex-1 bg-cosmos-700 px-2 py-1 rounded text-sm placeholder-cosmos-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
        />
        <button
          type="button"
          onClick={sendChat}
          className="px-3 py-1 rounded bg-amber-500 text-cosmos-950 font-bold text-sm hover:bg-amber-400"
        >
          Send
        </button>
      </div>

      {/* Match controls */}
      <div className="border-t border-cosmos-700 p-2 flex gap-1">
        <button
          type="button"
          onClick={onDeclareVictory}
          className="flex-1 py-1.5 rounded bg-emerald-600 text-cosmos-50 text-sm font-bold hover:bg-emerald-500"
        >
          Declare Victory
        </button>
        <button
          type="button"
          onClick={onConcede}
          className="flex-1 py-1.5 rounded bg-rose-700 text-cosmos-50 text-sm font-bold hover:bg-rose-600"
        >
          Concede
        </button>
      </div>
    </aside>
  );
}

function ChatTab({ chat, viewer }: { chat: readonly ChatEntry[]; viewer: PlayerId }) {
  return (
    <ul className="space-y-1 text-sm">
      {chat.length === 0 && <li className="text-cosmos-400 italic">No messages yet.</li>}
      {chat.map((entry) => (
        <li
          key={`${entry.at}-${entry.actor}`}
          className={clsx('flex gap-2', entry.actor === viewer && 'justify-end')}
        >
          <span className="font-bold text-amber-300">{shortName(entry.actor)}:</span>
          <span className="text-cosmos-100">{entry.text}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Format pattern shared with the competitor's playable surface:
 *   `<ACTOR> — <event_type> — R<round> • <description>`
 * Round is a placeholder until P1 introduces enforced round structure.
 */
function logEntry({
  side,
  event,
  round,
  description,
}: {
  side: 'OPP' | 'YOU';
  event: string;
  round: number;
  description: string;
}): string {
  return `${side} — ${event} — R${round} • ${description}`;
}

function LogTab({
  dice,
  chat,
  viewer,
}: {
  dice: readonly DiceRoll[];
  chat: readonly ChatEntry[];
  viewer: PlayerId;
}) {
  // Round 0 placeholder until the rule-enforcing engine tracks the
  // 7-round structure (catch-up P1). Once that lands, replace with the
  // active round number from public state.
  const round = 0;
  const merged: Array<{ at: number; side: 'OPP' | 'YOU'; text: string }> = [
    ...chat.map((c) => ({
      at: c.at,
      side: (c.actor === viewer ? 'YOU' : 'OPP') as 'OPP' | 'YOU',
      text: logEntry({
        side: c.actor === viewer ? 'YOU' : 'OPP',
        event: 'Chat',
        round,
        description: c.text,
      }),
    })),
    ...dice.map((d) => ({
      at: d.at,
      side: (d.actor === viewer ? 'YOU' : 'OPP') as 'OPP' | 'YOU',
      text: logEntry({
        side: d.actor === viewer ? 'YOU' : 'OPP',
        event: 'Roll',
        round,
        description: `d${d.sides} = ${d.result}`,
      }),
    })),
  ].sort((a, b) => a.at - b.at);

  const [filter, setFilter] = useState<'all' | 'OPP' | 'YOU'>('all');
  const filtered = filter === 'all' ? merged : merged.filter((e) => e.side === filter);

  return (
    <div className="space-y-2">
      <div className="flex gap-1 text-[11px]">
        {(['all', 'YOU', 'OPP'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded px-2 py-0.5 ${
              filter === f
                ? 'bg-amber-600 text-white'
                : 'bg-cosmos-800 text-cosmos-300 hover:bg-cosmos-700'
            }`}
          >
            {f === 'all' ? 'All' : f}
          </button>
        ))}
      </div>
      <ul className="space-y-1 font-mono text-xs text-cosmos-300">
        {filtered.length === 0 && <li className="italic">No events yet.</li>}
        {filtered.map((item) => (
          <li key={`${item.at}-${item.text}`}>{item.text}</li>
        ))}
      </ul>
    </div>
  );
}

function DiceTab({ dice, onRoll }: { dice: readonly DiceRoll[]; onRoll: (sides: number) => void }) {
  return (
    <div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[4, 6, 8, 10, 12, 20].map((sides) => (
          <button
            key={sides}
            type="button"
            onClick={() => onRoll(sides)}
            className="group relative aspect-square rounded-md bg-cosmos-800 ring-1 ring-cosmos-700 transition hover:ring-amber-400/60 hover:scale-105"
            title={`Roll d${sides}`}
          >
            <img
              src={`/assets/wonders/dice-d${sides}.png`}
              alt={`d${sides}`}
              className="absolute inset-0 h-full w-full rounded-md object-cover opacity-90 group-hover:opacity-100"
              draggable={false}
            />
            <span className="absolute bottom-0.5 right-1 rounded bg-cosmos-950/80 px-1 text-[9px] font-bold font-mono text-amber-300">
              d{sides}
            </span>
          </button>
        ))}
      </div>
      <h4 className="text-xs text-cosmos-400 uppercase mb-1">Recent rolls</h4>
      <ul className="space-y-0.5 text-xs">
        {dice.length === 0 && <li className="text-cosmos-400 italic">No rolls yet.</li>}
        {dice
          .slice(-15)
          .reverse()
          .map((d) => (
            <li key={`${d.at}-${d.actor}`}>
              <span className="text-amber-300 font-bold">{d.result}</span>{' '}
              <span className="text-cosmos-400">on d{d.sides}</span>{' '}
              <span className="text-cosmos-500">— {shortName(d.actor)}</span>
            </li>
          ))}
      </ul>
    </div>
  );
}

function shortName(player: PlayerId): string {
  // Trim WorkOS user IDs (user_01...) to a short label. The actual
  // display name comes from /api/me later.
  return player.length > 12 ? `${player.slice(0, 8)}…` : player;
}
